import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  BucketInfo,
  Destination,
  GoogleStorageClass,
  GoogleStorageClassEnum,
  StorageAccountRegion,
  StorageAccountV2
} from 'mbs-ui-shared@models/StorageAccount';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, tap } from 'rxjs/operators';
import { BehaviorSubject, of, switchMap } from 'rxjs';
import { StorageAccountsService } from 'mbs-ui-shared@services/storage-accounts/storage-accounts.service';
import { PermissionsEnum, StorageType } from 'mbs-ui-shared';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MbsPopupType, MbsUiKitModule } from 'mbs-ui-kit';
import { I18NextModule, I18NextPipe } from 'angular-i18next';
import { PagedSelectModule } from '@components/paged-select/paged-select.module';
import { CommonModule } from '@angular/common';
import { AbilityModule, AbilityService } from 'ability';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import {
  BucketNameValidator,
  BucketsListingRestrictedPossibility
} from '@components/add-destination-form/add-destination-form.constants';
import { amazonS3GlacierStorageTypes, googleCloudStorageTypes } from 'mbs-ui-shared@constants/storage.constants';
import { CompanySelectComponent } from '@components/paged-select/company-select.component';

@UntilDestroy()
@Component({
  selector: 'mbs-add-destination-form',
  templateUrl: './add-destination-form.component.html',
  styleUrls: ['./add-destination-form.component.scss'],
  imports: [
    I18NextModule,
    MbsUiKitModule,
    PagedSelectModule,
    CommonModule,
    ReactiveFormsModule,
    NgbTooltipModule,
    AbilityModule
  ],
  standalone: true
})
export class AddDestinationFormComponent implements OnInit {
  public readonly mbsPopupType = MbsPopupType;
  public readonly googleCloudAccounts = [
    StorageType.GoogleCloud,
    StorageType.Google
  ];
  public readonly permissionsEnum = PermissionsEnum;
  public readonly storageType = StorageType;

  public storageClasses: GoogleStorageClass[] = null;
  public googleSubregions: StorageAccountRegion[] = null;
  public bucketsLoading = false;
  public currentBucket: BucketInfo;
  public regions: StorageAccountRegion[];
  public specificGCDataLoading = false;
  public bucketsNames: { bucketName: string; cloudBucketID?: string }[];
  public bucketsLoaded = false;
  public queries$ = new BehaviorSubject([]);

  public displayNameControl: FormControl;
  public bucketNameControl: FormControl;
  public storageClassControl: FormControl;
  public regionControl: FormControl;
  public googleDestinationSubregionControl: FormControl;
  public immutabilityControl: FormControl;
  public companiesControl: FormControl;
  public s3AcceleratorControl: FormControl;
  public reuseAccountRules: FormControl;
  public isExternalBucketControl: FormControl;
  public isWizard = false;
  public _storageAccount: StorageAccountV2 = null;
  public selectedCompanyIds = [];
  public bucketsListingRestricted: BucketsListingRestrictedPossibility = null;
  public bucketNameRules = [];
  public blockForm = false;
  public manualBucketLoading = false;

  private regionField: FormControl;
  private storageClassField: FormControl;

  @Input() form: FormGroup;
  @Input() createNew: FormControl;
  @Input() destination: Destination;
  // bucket, container or root folder
  @Input() bucketTypeName: string;
  @Input() viewMode: 'modal' | 'wizard' = 'modal';

  @Input() set storageAccount(account: StorageAccountV2) {
    if (!account?.id || account?.id === this._storageAccount?.id) return;

    this.onStorageAccountDefined(account);
  }

  @Output() criticalError = new EventEmitter<string>();
  @Output() currentBucketChanged = new EventEmitter<BucketInfo>();
  @Output() formUnstable = new EventEmitter<boolean>(false);
  @Output() initialLoading = new EventEmitter<boolean>(true);

  @ViewChild(CompanySelectComponent) companySelect: CompanySelectComponent;

  constructor(
    private storageAccountService: StorageAccountsService,
    private i18N: I18NextPipe,
    public ability: AbilityService
  ) {}

  ngOnInit() {
    this.isWizard = this.viewMode === 'wizard';
    this.queries$.pipe(untilDestroyed(this)).subscribe((val) => this.formUnstable.emit(!!val?.length));
  }

  private setBucketNameValidators() {
    const getBucketNames = (key: string): string[] => {
      return Object.values(this.i18N.transform.call(this.i18N, key, { returnObjects: true }));
    };

    if (this._storageAccount.storageType === StorageType.Azure) {
      this.bucketNameControl.addValidators([
        Validators.pattern(BucketNameValidator.Azure),
        Validators.minLength(3),
        Validators.maxLength(63)
      ]);
      this.bucketNameRules = getBucketNames('shared:modals.addDestinationBucket.bucketNameRules.azure');
    } else if (this._storageAccount.storageType === StorageType.BackblazeB2) {
      this.bucketNameControl.addValidators([Validators.pattern(BucketNameValidator.B2), Validators.minLength(6), Validators.maxLength(63)]);
      this.bucketNameRules = getBucketNames('shared:modals.addDestinationBucket.bucketNameRules.b2');
    } else if (
      amazonS3GlacierStorageTypes.includes(this._storageAccount.storageType) ||
      this._storageAccount.storageType === StorageType.UbAmazonS3 ||
      this._storageAccount.storageType === StorageType.S3Compatible ||
      this._storageAccount.storageType === StorageType.Minio
    ) {
      this.bucketNameControl.addValidators([Validators.pattern(BucketNameValidator.S3), Validators.minLength(3), Validators.maxLength(63)]);
      this.bucketNameRules = getBucketNames('shared:modals.addDestinationBucket.bucketNameRules.s3');
    } else if (this._storageAccount.storageType === StorageType.Wasabi || this._storageAccount.storageType === StorageType.CblWasabi) {
      this.bucketNameControl.addValidators([
        Validators.pattern(BucketNameValidator.Wasabi),
        Validators.minLength(3),
        Validators.maxLength(63)
      ]);
      this.bucketNameRules = getBucketNames('shared:modals.addDestinationBucket.bucketNameRules.wasabi');
    } else if (googleCloudStorageTypes.includes(this._storageAccount.storageType)) {
      this.bucketNameControl.addValidators([
        Validators.pattern(BucketNameValidator.Google),
        Validators.minLength(3),
        Validators.maxLength(222)
      ]);
      this.bucketNameRules = getBucketNames('shared:modals.addDestinationBucket.bucketNameRules.google');
    } else {
      this.bucketNameControl.addValidators([Validators.minLength(1), Validators.maxLength(255)]);
      this.bucketNameRules = getBucketNames('shared:modals.addDestinationBucket.bucketNameRules.common');
    }
  }

  public isManualBucketEntering() {
    return this._storageAccount.isPrivateStorage || this.bucketsListingRestricted || this.isExternalBucketControl.value;
  }

  public getManualBucketEnteringAlertText(): string {
    if (this._storageAccount.isPrivateStorage) {
      return this.i18N.transform('shared:modals.addDestinationBucket.privateStorageAlert', {
        name: this._storageAccount.displayName,
        type: this.bucketTypeName
      });
    }

    if (this.bucketsListingRestricted === BucketsListingRestrictedPossibility.Maybe) {
      return this.i18N.transform('shared:modals.addDestinationBucket.bucketsListingMaybeRestrictedAlert', {
        type: this.bucketTypeName,
        format: 'capitalize'
      });
    }

    if (this.bucketsListingRestricted === BucketsListingRestrictedPossibility.Exactly) {
      return this.i18N.transform('shared:modals.addDestinationBucket.bucketsListingExactlyRestrictedAlert', { type: this.bucketTypeName });
    }

    return '';
  }

  private onStorageAccountDefined(account: StorageAccountV2) {
    this.defineControls();
    this._storageAccount = account;

    of(account)
      .pipe(
        tap((account) => {
          account.isPrivateStorage && this.createNew.patchValue(false);
        }),
        switchMap(() => {
          return this.googleCloudAccounts.includes(this._storageAccount.storageType)
            ? this.storageAccountService.getGoogleStorageClasses(this.isWizard)
            : of(null);
        }),
        tap((storageClasses) => (this.storageClasses = storageClasses)),
        switchMap(() => {
          return this._storageAccount.supportedBucketLocation && !this.googleCloudAccounts.includes(this._storageAccount.storageType)
            ? this.storageAccountService.getStorageAccountRegions({
                storageAccountId: this._storageAccount.id,
                fromWizard: this.isWizard
              })
            : of(null);
        })
      )
      .subscribe((regions) => {
        this._storageAccount?.supportedBucketLocation && this.regionControl.setValidators([Validators.required]);
        this._storageAccount?.supportedS3Accelerator && this.s3AcceleratorControl.patchValue(false);
        this.addSubscriptions();

        if (this.destination) {
          this.updateFormByExistingDestination();
        } else {
          this.onCreateNewChanged(this.createNew?.value);
        }
        this.updateValidators();
        this.initialLoading.emit(false);
        this.regions = regions;
      });
  }

  private defineControls() {
    this.regionField = this.form.get('region') as FormControl;
    this.storageClassField = this.form.get('storageClass') as FormControl;
    this.displayNameControl = this.form.get('displayName') as FormControl;
    this.bucketNameControl = this.form.get('bucketName') as FormControl;
    this.storageClassControl = this.form.get('storageClass') as FormControl;
    this.regionControl = this.form.get('region') as FormControl;
    this.googleDestinationSubregionControl = this.form.get('googleDestinationSubregion') as FormControl;
    this.immutabilityControl = this.form.get('immutability') as FormControl;
    this.companiesControl = this.form.get('companies') as FormControl;
    this.s3AcceleratorControl = this.form.get('s3Accelerator') as FormControl;
    this.reuseAccountRules = this.form.get('reuseAccountRules') as FormControl;
    this.isExternalBucketControl = this.form.get('isExternalBucket') as FormControl;
  }

  private addSubscriptions() {
    this.createNew.valueChanges.pipe(untilDestroyed(this)).subscribe((val) => this.onCreateNewChanged(val));
    this.subscribeToBucketNameChanged();
    this.subscribeToManualEnteredBucketNameChanged();
    this.createStorageClassFieldSubscription();
    this.createRegionFieldSubscription();
    !this.blockForm && this.externalBucketSubscription();
  }

  private subscribeToBucketNameChanged() {
    this.bucketNameControl.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged(), filter(Boolean)).subscribe((val) => {
      if (this.createNew.value || val === this.currentBucket?.bucketName || this?.blockForm) return;

      if (this.isManualBucketEntering()) {
        return void this.currentBucketChanged.emit({
          bucketName: this.form.get('bucketName')?.value
        });
      }

      this.onBucketNameChanged(val);
    });
  }

  private subscribeToManualEnteredBucketNameChanged() {
    this.bucketNameControl.valueChanges
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        tap((val) => {
          if (this._storageAccount.isPrivateStorage) {
            this.currentBucketChanged.emit({
              bucketName: val
            });
          }
        }),
        filter(() => this.bucketsListingRestricted || this.isExternalBucketControl.value),
        debounceTime(1000)
      )
      .subscribe((val) => {
        if (!val) {
          this.currentBucket = null;
          this.currentBucketChanged.emit(null);

          return;
        }

        this.manualBucketLoading = true;
        this.onBucketNameChanged(val);
      });
  }

  private externalBucketSubscription() {
    this.isExternalBucketControl.valueChanges.subscribe((val) => {
      !this.createNew?.value && this.bucketNameControl.patchValue(val ? null : this.currentBucket?.bucketName || this.destination?.bucket?.bucketName || null);
    });
  }

  private onBucketNameChanged(bucketName: string) {
    this.currentBucket = null;
    this.currentBucketChanged.emit(null);
    this.bucketsLoading = true;
    this.addQuery('bucketName');

    const needDetailsRequest = this._storageAccount.supportedBucketLocation || this._storageAccount.immutabilitySupported;
    const request$ = needDetailsRequest
      ? this.storageAccountService.getBucketInfo(this._storageAccount.id, bucketName, this.isWizard)
      : of((this.bucketsNames || []).find(({ bucketName: name }) => name === bucketName));

    request$.pipe(
      finalize(() => {
        this.bucketsLoading = false;
        this.manualBucketLoading = false;
        this.removeQuery('bucketName');
      })
    ).subscribe((res) => {
      this.companySelect?.close();
      this.currentBucket = needDetailsRequest
        ? { ...res, cloudBucketID: this.bucketsNames.find(({ bucketName: name  }) => name === bucketName)?.cloudBucketID }
        : res;

        this.currentBucketChanged.emit(this.currentBucket);
        this._storageAccount.supportedS3Accelerator && this.s3AcceleratorControl.patchValue(this.currentBucket?.s3AcceleratorEnabled);

        /*
        If OL is not supported by bucket need to switch it off, but not in the case where there are no rights to control Ol.
        In this case, we will get the expected conflict when saving
      */
        if (
          this.ability.can('read', PermissionsEnum.ManageImmutability) &&
          this._storageAccount.immutabilitySupported &&
          !(res as BucketInfo)?.immutabilitySupported
        ) {
          this.form.get('immutability').patchValue(false);
        }
      });
  }

  private addQuery(queryName: string) {
    this.queries$.next([...this.queries$.value, queryName]);
  }

  private removeQuery(queryName: string) {
    this.queries$.next(this.queries$.value.filter((name) => name !== queryName));
  }

  private createStorageClassFieldSubscription() {
    this.storageClassField?.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe((val) => {
      if (!this.createNew.value || !val?.id) return;

      this.specificGCDataLoading = true;
      this.addQuery('storageClass');

      if (val.id === GoogleStorageClassEnum.MultiRegional) {
        this.googleDestinationSubregionControl.disable();
      } else {
        this.googleDestinationSubregionControl.enable();
      }

      this.storageAccountService
        .getStorageAccountRegions({
          storageAccountId: this._storageAccount.id,
          googleStorageClass: val.id,
          fromWizard: this.isWizard
        })
        .pipe(
          finalize(() => {
            this.specificGCDataLoading = false;
            this.removeQuery('storageClass');
          })
        )
        .subscribe((regions) => {
          this.regionField.patchValue(null);
          this.googleDestinationSubregionControl.patchValue(null);

          this.regions = regions;
          this.googleSubregions = null;
        });
    });
  }

  private createRegionFieldSubscription() {
    if (!this.googleCloudAccounts.includes(this._storageAccount.storageType)) return;

    this.regionField.valueChanges
      .pipe(
        untilDestroyed(this),
        filter((val) => Boolean(val)),
        distinctUntilChanged()
      )
      .subscribe((val) => {
        if (!this.createNew.value) return;

        this.specificGCDataLoading = true;
        this.addQuery('region');

        this.storageAccountService
          .getGoogleSubregions(val.systemName, this.storageClassField.value.id, this.isWizard)
          .pipe(
            finalize(() => {
              this.specificGCDataLoading = false;
              this.removeQuery('region');
            })
          )
          .subscribe((subRegions) => {
            const subRegionsSystemNames = subRegions.map((subregion) => subregion.systemName);
            if (
              this.googleDestinationSubregionControl.value &&
              !subRegionsSystemNames.includes(this.googleDestinationSubregionControl.value?.systemName)
            ) {
              this.googleDestinationSubregionControl.patchValue(null);
            }

            this.googleSubregions = subRegions;
          });
      });
  }

  private updateFormByExistingDestination() {
    this.createNew.patchValue(false);
    this.form.patchValue({
      displayName: this.destination.displayName,
      immutability: this.destination?.immutabilityEnabled,
      isExternalBucket: this.destination?.isExternalBucket
    });

    if (this.destination?.isReadOnly) {
      this.blockForm = true;
      this.form.patchValue({
        bucketName: this.destination?.bucket?.bucketName
      });
      this.form?.disable();
    } else {
      this.selectedCompanyIds = this.destination?.assignedCompanies || [];
    }
  }

  private updateValidators() {
    this._storageAccount.supportedBucketLocation && this.regionField.setValidators([Validators.required]);
    this._storageAccount.supportedStorageClass && this.createNew.value && this.storageClassField.setValidators([Validators.required]);
    this.googleCloudAccounts.includes(this._storageAccount.storageType) &&
      this.createNew.value &&
      this.googleDestinationSubregionControl.setValidators([Validators.required]);
    this.storageClassField?.updateValueAndValidity();
    this.googleDestinationSubregionControl?.updateValueAndValidity();
  }

  private onCreateNewChanged(val: boolean) {
    this.bucketNameControl.reset();
    this.ability.can('read', PermissionsEnum.ManageImmutability) && this.form.get('immutability').reset();

    if (val) {
      this.applyCreateNewBucket();
    } else {
      this.applyChooseExistingBucket();
    }

    this.regionField.updateValueAndValidity();
    this.googleDestinationSubregionControl.updateValueAndValidity();
    this.storageClassField.updateValueAndValidity();
  }

  private applyCreateNewBucket() {
    const immutability = this.ability.can('read', PermissionsEnum.ManageImmutability) ? true : !!this?.destination?.immutabilityEnabled;
    this._storageAccount.supportedBucketLocation && this.regionField.setValidators([Validators.required]);
    this._storageAccount.supportedStorageClass && this.storageClassField.setValidators([Validators.required]);
    this.googleCloudAccounts.includes(this._storageAccount.storageType) &&
      this.googleDestinationSubregionControl.setValidators([Validators.required]);
    this._storageAccount.immutabilitySupported && this.immutabilityControl.patchValue(immutability);
    this.isExternalBucketControl.patchValue(null);
    this.setBucketNameValidators();
  }

  private applyChooseExistingBucket() {
    this.regionField?.clearValidators();
    this.googleDestinationSubregionControl?.clearValidators();
    this.storageClassField?.clearValidators();
    this.bucketNameControl?.setValidators([Validators.required]);

    if (!this.bucketsLoaded && !this.isManualBucketEntering() && !this.blockForm) {
      this.loadBucketsList();
    } else {
      // in case of Manual Entering current bucket is unloaded
      const bucketName = this.currentBucket?.bucketName || this.destination?.bucket?.bucketName;
      bucketName && this.bucketNameControl.patchValue(bucketName);
    }
  }

  private loadBucketsList() {
    this.bucketsLoading = true;
    this.addQuery('bucketList');
    this.storageAccountService
      .getBucketsNames({
        storageAccountId: this._storageAccount.id,
        fromWizard: this.isWizard,
        skipError: true
      })
      .pipe(
        catchError((err) => {
          if (err?.error.typeCode === 4500) {
            this.bucketsListingRestricted = BucketsListingRestrictedPossibility.Exactly;

            return of(null);
          }

          this.criticalError.next(err?.error?.detail);
          this.blockForm = true;
          this.form?.disable();
          throw err;
        }),
        finalize(() => {
          this.bucketsLoading = false;
          this.bucketsLoaded = true;
          this.removeQuery('bucketList');
        })
      )
      .subscribe((res) => {
        this.bucketsNames = res;

        if (res && !res.length) {
          this.bucketsListingRestricted = BucketsListingRestrictedPossibility.Maybe;
        }

        if (this.destination) {
          this.bucketNameControl.patchValue(this.destination?.bucket?.bucketName);
        }
      });
  }
}
